import {
    Alert,
    DialogBody,
    DialogStep,
    MultistepDialog,
    OverlayToaster,
    Position,
    Spinner
} from "@blueprintjs/core";
import {
    segmentTrackRestudyCreated,
    segmentTrackRestudyStepViewed,
    segmentTrackRestudySubmitted,
    segmentTrackRestudyUnchangedConfirmationConfirmed,
    segmentTrackRestudyUnchangedConfirmationShown
} from "analytics/analyticTrackEvents";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    GetQueueProjectsQuery,
    useGetPoiBusesByNiraBusIdLazyQuery
} from "generated/graphql";
import { loader } from "graphql.macro";
import { getHasuraDataAndConvertToNiraType } from "graphql/helpers/queryHelpers";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { GeneratorType } from "types/generatorType";
import { Loading } from "types/loadingType";
import {
    convertHasuraQueueProjectsByFuelType,
    QueueProject
} from "types/queueProjectsType";
import { RegionType } from "types/regionType";
import { getScopeFromScopes, Scope, ScopeName } from "types/scope";
import {
    getCapacityCostsScopeViewFromRegionAndGenerator,
    getGenerationFuelTypesForRegion
} from "types/scopeViewType";
import {
    QueueProjectsByFuelType,
    queueProjectTogglesEqual,
    validateQueueProjectsByFuelType
} from "utils/queueProjectUtils";
import {
    areSpeculativeProjectDraftsValid,
    buildPoiEligibilityFilters,
    clearSpeculativeProjectDraftPois,
    restoreSpeculativeProjectDraftPois,
    SpeculativeProjectDraft,
    toSpeculativePoiBusesByNiraBusId
} from "utils/speculativeProjectUtils";
import DetailsPanel from "./DetailsPanel";
import NewProjectsPanel from "./NewProjectsPanel";
import "./NewScopeDialog.scss";
import QueuedGenPanel from "./QueuedGenPanel";
import SelectBasePanel from "./SelectBasePanel";
import {
    displayNameToScopeName,
    useCreateUserScope,
    ValidatedNewScopeRequest
} from "./useCreateUserScope";

const GET_QUEUE_PROJECTS = loader("src/graphql/getQueueProjects.graphql");

const NewScopeToaster = OverlayToaster.create({
    position: Position.TOP
});

const BASE_SCOPE_STEP_ID = "base-scope";

export interface NewScopeRequest {
    displayName: string;
    notes: string;
    selectedFuelTypes: ReadonlyArray<GeneratorType>;
    baseScope: Scope | undefined;
    editedQueueProjects: ReadonlyArray<QueueProject> | undefined;
    // Speculative ("new") projects that aren't in the base queue file.
    newProjects: ReadonlyArray<SpeculativeProjectDraft>;
}

const validateRequest = (
    request: NewScopeRequest,
    teamName: string,
    niraBusId: string,
    existingScopeNames: ReadonlyArray<string>
): ValidatedNewScopeRequest | undefined => {
    const scopeNameResult = displayNameToScopeName(
        request.displayName,
        teamName,
        niraBusId,
        existingScopeNames
    );
    if (
        !request.baseScope ||
        !request.editedQueueProjects ||
        !scopeNameResult.success ||
        request.selectedFuelTypes.length === 0 ||
        !areSpeculativeProjectDraftsValid(request.newProjects)
    ) {
        return undefined;
    }
    return {
        displayName: request.displayName,
        notes: request.notes,
        selectedFuelTypes: request.selectedFuelTypes,
        baseScope: request.baseScope,
        editedQueueProjects: request.editedQueueProjects,
        newProjects: request.newProjects
    };
};

const EMPTY_NEW_SCOPE_REQUEST: NewScopeRequest = {
    displayName: "",
    notes: "",
    selectedFuelTypes: [],
    baseScope: undefined,
    editedQueueProjects: undefined,
    newProjects: []
};

interface NewScopeDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const useQueueProjectsByFuelType = (
    selectedFuelTypes: ReadonlyArray<GeneratorType>,
    baseScope: Scope | undefined
): Loading<QueueProjectsByFuelType> | undefined => {
    const { bus } = useBusPageConfiguration();
    const region = bus.region;

    const scopeViews = selectedFuelTypes.map((fuelType) =>
        getCapacityCostsScopeViewFromRegionAndGenerator(region, fuelType)
    );
    const uniqueScopeViews = Array.from(new Set(scopeViews));

    const skip = !baseScope || uniqueScopeViews.length === 0;

    const queryResult = getHasuraDataAndConvertToNiraType(
        GET_QUEUE_PROJECTS,
        (data: GetQueueProjectsQuery) =>
            convertHasuraQueueProjectsByFuelType(
                data,
                selectedFuelTypes,
                region
            ),
        {
            // Fallback to "" should never happen — skip is true when baseScope is undefined
            scope: baseScope?.scope ?? "",
            scope_views: uniqueScopeViews,
            region
        },
        skip
    );

    if (skip) {
        return undefined;
    }
    if (queryResult === "loading") {
        return "loading";
    }

    return queryResult;
};

const NewScopeDialog: React.FC<NewScopeDialogProps> = ({ isOpen, onClose }) => {
    const { allScopesForBus, niraBusId, bus } = useBusPageConfiguration();
    const { teamName } = useUserDataContext();
    const existingScopeNames = allScopesForBus.map((s) => s.scope);
    const makeEmptyRequest = (): NewScopeRequest => ({
        ...EMPTY_NEW_SCOPE_REQUEST,
        selectedFuelTypes: getGenerationFuelTypesForRegion(bus.region)
    });
    const [request, setRequest] = useState<NewScopeRequest>(makeEmptyRequest);
    const [isConfirming, setIsConfirming] = useState(false);

    // Read at click time rather than captured in the Alert's onConfirm closure:
    // a second Run anyway click, before React re-renders the closing Alert,
    // then finds nothing to submit.
    const confirmationRef = useRef<ValidatedNewScopeRequest>();
    const clearConfirmation = useCallback(() => {
        confirmationRef.current = undefined;
        setIsConfirming(false);
    }, []);

    // A CSV can finish parsing after the user has reached the confirmation.
    useEffect(() => {
        clearConfirmation();
    }, [clearConfirmation, request]);

    // Speculative generation is a WECC-only contract for now: the pipeline
    // only reads `is_speculative` rows for WECC user scopes.
    const supportsNewProjects = bus.region === RegionType.WECC;

    // Which "New projects" rows have been through a failed Next. Blueprint
    // spreads nextButtonProps last, so supplying onClick replaces its
    // step-change handler — we use that to hold the user on the step and
    // reveal the errors, instead of greying out Next with no explanation.
    const [validatedNewProjectIds, setValidatedNewProjectIds] = useState<
        ReadonlySet<string>
    >(() => new Set());

    const { createUserScope, isSubmitting } = useCreateUserScope();

    const queueProjectsByFuelType:
        | Loading<QueueProjectsByFuelType>
        | undefined = useQueueProjectsByFuelType(
        request.selectedFuelTypes,
        request.baseScope
    );

    // Memoized: this array is a dependency of the POI search effect in
    // NewProjectsPanel, so a new identity on every render would restart the
    // search debounce while the user types.
    const selectedScopeViews = useMemo(
        () =>
            Array.from(
                new Set(
                    request.selectedFuelTypes.map((ft) =>
                        getCapacityCostsScopeViewFromRegionAndGenerator(
                            bus.region,
                            ft
                        )
                    )
                )
            ),
        [request.selectedFuelTypes, bus.region]
    );

    // Re-resolves the New projects rows' POIs when the base scope changes.
    // Lazy rather than a useQuery: it runs once per change, from the handler
    // below, not on every render of the dialog.
    const [getPoiBusesByNiraBusId] = useGetPoiBusesByNiraBusIdLazyQuery();

    // `nira_bus_id` of the POI each row held when a base-scope change blanked
    // it, keyed by row id — the lookup key the restore needs. A ref because it
    // is never rendered, and because it has to survive several base-scope
    // changes in a row: the second change sees rows the first already blanked,
    // which no longer carry a POI to re-resolve. Entries are harmless once
    // restored — the restore skips a row that has a POI — and are dropped when
    // the dialog resets.
    const poiNiraBusIdsByDraftId = useRef(new Map<string, string>());

    const handleBaseScopeChange = async (scopeName: ScopeName) => {
        const scope = getScopeFromScopes(allScopesForBus, scopeName);
        const notes = scope
            ? `This study is based on the ${scope.display_name} scope. Data freshness at time of creation:\n\n${scope.data_freshness_markdown}`
            : "";
        // A POI bus was picked against the previous base scope's models and can
        // be absent from the new one, so it can't come across unchecked — that
        // would let Create insert a nira_bus_id the pipeline can't find. Blank
        // the POIs now (every row keeps its name, fuel type and sizes) and put
        // back the ones the new scope has below, rather than discarding rows
        // the user typed. Built from `request` rather than the updater's `prev`
        // because the dropped buses are needed outside the update; only the
        // step-1 select reaches here, so there is no concurrent draft edit.
        const cleared = clearSpeculativeProjectDraftPois(request.newProjects);
        cleared.niraBusIdsByDraftId.forEach((niraBusId, draftId) =>
            poiNiraBusIdsByDraftId.current.set(draftId, niraBusId)
        );
        setRequest({
            ...request,
            baseScope: scope,
            notes,
            editedQueueProjects: undefined,
            newProjects: cleared.drafts
        });
        setValidatedNewProjectIds(new Set());

        // Nothing to resolve: no row has ever had a POI, or — with no selected
        // views — no bus could qualify anyway, since the empty `_and` an empty
        // filter list produces reads as "no restriction" in Hasura, which would
        // resolve every POI against no base scope at all.
        if (
            scope === undefined ||
            poiNiraBusIdsByDraftId.current.size === 0 ||
            selectedScopeViews.length === 0
        ) {
            return;
        }

        const result = await getPoiBusesByNiraBusId({
            variables: {
                region: bus.region,
                nira_bus_ids: Array.from(
                    new Set(poiNiraBusIdsByDraftId.current.values())
                ),
                // The bus must be in the new base scope in every view this
                // scope runs, and in the study bus's utility: exactly what the
                // step's own typeahead requires of a POI.
                scope_view_filters: [
                    ...buildPoiEligibilityFilters(
                        scope.scope,
                        selectedScopeViews,
                        bus
                    )
                ]
            }
        });

        if (result.error !== undefined) {
            // Which POIs the new scope has is unknown, so they stay blanked
            // rather than carried over unverified. Say so: the rows report the
            // bus as missing from the scope, which isn't the whole story here.
            NewScopeToaster.show({
                message: `Could not check the new projects' POIs against ${scope.display_name}. Pick them again.`,
                intent: "warning"
            });
            return;
        }

        const poiBusesByNiraBusId = toSpeculativePoiBusesByNiraBusId(
            result.data?.buses ?? []
        );

        setRequest((prev) =>
            // The base scope can change again while this is in flight; POIs
            // resolved against a scope that is no longer selected must not be
            // put back.
            prev.baseScope?.scope !== scope.scope
                ? prev
                : {
                      ...prev,
                      newProjects: restoreSpeculativeProjectDraftPois(
                          prev.newProjects,
                          poiNiraBusIdsByDraftId.current,
                          poiBusesByNiraBusId
                      )
                  }
        );
    };

    const handleEditedQueueProjectsChanged = (
        projects: ReadonlyArray<QueueProject>
    ) => {
        setRequest((prev) => ({
            ...prev,
            editedQueueProjects: projects
        }));
    };

    const handleReset = () => {
        setRequest(makeEmptyRequest());
        clearConfirmation();
        setValidatedNewProjectIds(new Set());
        poiNiraBusIdsByDraftId.current = new Map();
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const wasOpenRef = useRef(false);

    // MultistepDialog's onChange only fires on step navigation, so without
    // this the first step would show zero views in the funnel. The dialog
    // stays mounted while closed, so only the closed-to-open transition
    // counts as a view.
    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            segmentTrackRestudyStepViewed(
                niraBusId,
                bus.region,
                BASE_SCOPE_STEP_ID
            );
        }
        wasOpenRef.current = isOpen;
    }, [isOpen, niraBusId, bus.region]);

    const handleCreate = async (validatedRequest: ValidatedNewScopeRequest) => {
        segmentTrackRestudySubmitted(
            niraBusId,
            bus.region,
            validatedRequest.baseScope.scope,
            validatedRequest.selectedFuelTypes.length
        );
        const created = await createUserScope(validatedRequest, "user");
        // Every submitted event must have a terminal created event so
        // funnel drop-off isn't overstated, even on validation failures.
        segmentTrackRestudyCreated(niraBusId, bus.region, "user", created);
        if (created) {
            handleClose();
        }
    };

    const configurationStepValid =
        request.selectedFuelTypes.length > 0 &&
        request.baseScope !== undefined &&
        queueProjectsByFuelType !== undefined &&
        queueProjectsByFuelType !== "loading" &&
        validateQueueProjectsByFuelType(queueProjectsByFuelType).valid;

    // The first fuel type's list stands in for all of them: the configuration
    // step only lets the user through when every selected fuel type has the
    // same projects.
    const baseProjects =
        queueProjectsByFuelType === undefined ||
        queueProjectsByFuelType === "loading"
            ? undefined
            : Array.from(queueProjectsByFuelType.values())[0];

    return (
        <MultistepDialog
            isOpen={isOpen}
            onClose={isConfirming ? clearConfirmation : handleClose}
            title="Re-study Bus"
            icon="add"
            className={
                // The "New projects" table needs more room than Blueprint's
                // default 500px dialog; only widen where the step exists.
                supportsNewProjects
                    ? "NewScopeDialog NewScopeDialog-wide"
                    : "NewScopeDialog"
            }
            onChange={(newStepId) =>
                segmentTrackRestudyStepViewed(
                    niraBusId,
                    bus.region,
                    String(newStepId)
                )
            }
            showCloseButtonInFooter
            finalButtonProps={{
                text: "Create",
                disabled:
                    isSubmitting ||
                    // Whether to prompt before creating is decided against
                    // the base projects, so hold Create until they have loaded.
                    !configurationStepValid ||
                    !validateRequest(
                        request,
                        teamName,
                        niraBusId,
                        existingScopeNames
                    ),
                loading: isSubmitting,
                onClick: () => {
                    const validated = validateRequest(
                        request,
                        teamName,
                        niraBusId,
                        existingScopeNames
                    );
                    if (validated) {
                        if (
                            validated.newProjects.length === 0 &&
                            baseProjects !== undefined &&
                            queueProjectTogglesEqual(
                                validated.editedQueueProjects,
                                baseProjects
                            )
                        ) {
                            confirmationRef.current = validated;
                            setIsConfirming(true);
                            segmentTrackRestudyUnchangedConfirmationShown(
                                niraBusId,
                                bus.region,
                                validated.baseScope.scope
                            );
                        } else {
                            handleCreate(validated);
                        }
                    }
                }
            }}
        >
            <DialogStep
                id={BASE_SCOPE_STEP_ID}
                title="Configuration"
                nextButtonProps={{
                    disabled: !configurationStepValid,
                    loading: queueProjectsByFuelType === "loading"
                }}
                panel={
                    <SelectBasePanel
                        selectedFuelTypes={request.selectedFuelTypes}
                        selectedBaseScope={request.baseScope}
                        onBaseScopeChange={handleBaseScopeChange}
                        queueProjectsByFuelType={queueProjectsByFuelType}
                    />
                }
            />
            <DialogStep
                id="queued-gen"
                title="Generator assumptions"
                nextButtonProps={{
                    disabled: request.editedQueueProjects === undefined
                }}
                panel={
                    queueProjectsByFuelType === undefined ||
                    queueProjectsByFuelType === "loading" ||
                    request.baseScope === undefined ? (
                        <DialogBody>
                            <Spinner />
                        </DialogBody>
                    ) : (
                        <QueuedGenPanel
                            queueProjectsByFuelType={queueProjectsByFuelType}
                            baseScopeLabel={request.baseScope.display_name}
                            editedQueueProjects={request.editedQueueProjects}
                            onEditedQueueProjectsChanged={
                                handleEditedQueueProjectsChanged
                            }
                        />
                    )
                }
            />
            {supportsNewProjects && (
                <DialogStep
                    id="new-projects"
                    title="New generators"
                    nextButtonProps={
                        areSpeculativeProjectDraftsValid(request.newProjects)
                            ? {}
                            : {
                                  onClick: () =>
                                      setValidatedNewProjectIds(
                                          new Set(
                                              request.newProjects.map(
                                                  (draft) => draft.id
                                              )
                                          )
                                      )
                              }
                    }
                    panel={
                        queueProjectsByFuelType === undefined ||
                        queueProjectsByFuelType === "loading" ||
                        request.baseScope === undefined ? (
                            <DialogBody>
                                <Spinner />
                            </DialogBody>
                        ) : (
                            <NewProjectsPanel
                                drafts={request.newProjects}
                                validatedDraftIds={validatedNewProjectIds}
                                baseScope={request.baseScope}
                                scopeViews={selectedScopeViews}
                                baseQueueProjects={baseProjects ?? []}
                                onDraftsChange={(newProjects) =>
                                    setRequest((prev) => ({
                                        ...prev,
                                        newProjects
                                    }))
                                }
                            />
                        )
                    }
                />
            )}
            <DialogStep
                id="details"
                title="Details"
                panel={
                    <>
                        <DetailsPanel
                            displayName={request.displayName}
                            notes={request.notes}
                            niraBusId={niraBusId}
                            existingScopeNames={existingScopeNames}
                            onDisplayNameChange={(displayName) =>
                                setRequest((prev) => ({ ...prev, displayName }))
                            }
                            onNotesChange={(notes) =>
                                setRequest((prev) => ({ ...prev, notes }))
                            }
                        />
                        <Alert
                            isOpen={isConfirming}
                            intent="warning"
                            icon="warning-sign"
                            confirmButtonText="Run anyway"
                            cancelButtonText="Cancel"
                            canEscapeKeyCancel
                            onCancel={clearConfirmation}
                            onConfirm={() => {
                                const confirmedRequest =
                                    confirmationRef.current;
                                clearConfirmation();
                                if (confirmedRequest) {
                                    segmentTrackRestudyUnchangedConfirmationConfirmed(
                                        niraBusId,
                                        bus.region,
                                        confirmedRequest.baseScope.scope
                                    );
                                    handleCreate(confirmedRequest);
                                }
                            }}
                        >
                            <p>
                                Your generator assumptions match the base scope
                                and no new generators were added. Do you still
                                want to run this study?
                            </p>
                        </Alert>
                    </>
                }
            />
        </MultistepDialog>
    );
};

export default NewScopeDialog;
